import { hasTeamRole, type RecordingDetail, type RecordingSummary, type Transfer } from '@greenroom/shared'
import { useMutation, useQueries, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { api, errorMessage } from '../lib/api'
import { useUser } from '../lib/auth'
import { q } from '../lib/queries'
import { recordingTitle } from './recording-list'
import { Button } from './ui/button'
import { Dialog } from './ui/dialog'
import { Field } from './ui/field'
import { Select } from './ui/select'

export function cacheRecording(qc: QueryClient, row: RecordingSummary, teamId: string, fromTeamId?: string) {
  for (const [key, list] of qc.getQueriesData<RecordingSummary[]>({ queryKey: ['teams'] })) {
    if (key[2] !== 'recordings' || !list || key[1] !== teamId && key[1] !== fromTeamId) continue
    const keep = list.filter((r) => r.id !== row.id)
    const belongs = key[1] === teamId && (key[3] === 'all' || key[3] === row.studioId)
    qc.setQueryData(key, belongs ? [row, ...keep] : keep)
  }
}
async function watchTransfer(qc: QueryClient, initial: Transfer) {
  let transfer = initial
  toast.loading('Duplicating recording…', { id: initial.id, duration: Infinity })
  try {
    while (!['done', 'failed'].includes(transfer.status)) {
      await new Promise((r) => setTimeout(r, 1500))
      transfer = await api<Transfer>(`/api/transfers/${initial.id}`)
      toast.loading(`Duplicating recording… ${transfer.bytesTotal ? Math.round(transfer.bytesCopied / transfer.bytesTotal * 100) : 0}%`, { id: initial.id, duration: Infinity })
    }
    if (transfer.status === 'failed') throw new Error(transfer.error ?? 'Duplicate failed')
    if (transfer.targetRecording) cacheRecording(qc, transfer.targetRecording, transfer.toTeamId)
    toast.success('Recording duplicated', { id: initial.id, duration: 6000 })
  } catch (err) { toast.error(errorMessage(err), { id: initial.id, duration: 10000 }) }
}
export function TransferDialog({ recording, kind, onClose }: { recording: RecordingDetail; kind: 'move' | 'duplicate'; onClose: () => void }) {
  const qc = useQueryClient(), { teams } = useUser()
  const writable = teams.filter((t) => hasTeamRole(t.role, 'producer'))
  const queries = useQueries({ queries: writable.map((t) => q.studios(t.id)) })
  const studios = queries.flatMap((query, i) => (query.data ?? []).map((s) => ({ ...s, teamName: writable[i]!.name, teamId: writable[i]!.id })))
  const [studioId, setStudioId] = useState<string>(), [title, setTitle] = useState(`Copy of ${recordingTitle(recording)}`)
  const key = useMemo(() => crypto.randomUUID(), [studioId, title])
  const selected = studios.find((s) => s.id === studioId)
  const action = useMutation<RecordingDetail | Transfer, Error, void>({
    mutationFn: () => kind === 'move'
      ? api<RecordingDetail>(`/api/recordings/${recording.id}/move`, { body: { studioId } })
      : api<Transfer>(`/api/recordings/${recording.id}/duplicate`, { body: { studioId, title }, headers: { 'Idempotency-Key': key } }),
    meta: { silent: true },
    onSuccess: (result) => {
      if (kind === 'move') {
        const moved = result as RecordingDetail
        qc.setQueryData(q.recording(moved.id).queryKey, moved)
        cacheRecording(qc, moved, moved.teamId, recording.teamId)
        toast.success('Recording moved')
      } else void watchTransfer(qc, result as Transfer)
      onClose()
    },
  })
  return <Dialog open onOpenChange={(open) => { if (!open && !action.isPending) onClose() }} title={kind === 'move' ? 'Move to…' : 'Duplicate to…'} footer={<Button variant="primary" loading={action.isPending} disabled={!studioId || kind === 'duplicate' && !title.trim()} onClick={() => action.mutate()}>{kind === 'move' ? 'Move recording' : 'Duplicate recording'}</Button>}>
    <div className="space-y-4">
      <Select label="Destination studio" placeholder="Choose a studio" value={studioId} onChange={setStudioId} options={studios.filter((s) => kind === 'duplicate' || s.id !== recording.studioId).map((s) => ({ value: s.id, label: s.name, group: s.teamName }))} />
      {kind === 'duplicate' && <Field label="Duplicate title" value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />}
      {kind === 'move' && selected && selected.teamId !== recording.teamId && <p className="text-sm text-warn">Members of the current team who are not in {selected.teamName} will lose access to this recording.</p>}
      {queries.some((q) => q.isError) && <p className="text-danger">Could not load studios.</p>}
      {action.isError && <p className="text-sm text-danger">{errorMessage(action.error)}</p>}
    </div>
  </Dialog>
}
